import { Page, PageBody, PageHeader, PageHeaderTitle, PageLoader } from '@customerio/pluma-components/react';
import { DocumentTitle } from '@/components/page/document-title';
import { ErrorPage } from '@/components/page/error';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import type { App } from '@/services/db/app/entity';
import { useApps } from '@/services/db/app/queries';
import { AppEditor } from './components/app-editor';
import { useAppEditor } from './hooks/use-app-editor';
import { useAppsBreadcrumbs, useAppsHrefs } from './hooks/use-apps-breadcrumbs';

type CreatePageProps = {
  workspaceId: number;
};

/**
 * The apps are read first because the form says when this one will claim the
 * workspace default. The error frame stands in only while that read has
 * produced nothing: once the form is up, a failed re-read leaves it, and
 * anything staged in it, alone.
 */
export default function CreatePage({ workspaceId }: CreatePageProps) {
  const appsQuery = useApps();
  const hrefs = useAppsHrefs(workspaceId);

  useAppsBreadcrumbs(workspaceId, {
    id: 'workspace_settings_apps_create',
    text: 'Add app',
    href: hrefs.create,
  });

  return (
    <>
      <DocumentTitle title="Add app" />
      <Page>
        <PageHeader>
          <PageHeaderTitle>Add app</PageHeaderTitle>
        </PageHeader>
        <PageBody>
          {appsQuery.data != null ? (
            <CreateAppForm workspaceId={workspaceId} apps={appsQuery.data} indexHref={hrefs.index} />
          ) : appsQuery.isError ? (
            <ErrorPage error={appsQuery.error} />
          ) : (
            <PageLoader />
          )}
        </PageBody>
      </Page>
    </>
  );
}

type CreateAppFormProps = {
  workspaceId: number;
  apps: App[];
  indexHref: string;
};

function CreateAppForm({ workspaceId, apps, indexHref }: CreateAppFormProps) {
  const safeNavigate = useSafeNavigate();
  const goToIndex = () => safeNavigate(indexHref);

  const editor = useAppEditor({ workspaceId, app: null, apps, onClose: goToIndex, onSaved: goToIndex });

  return <AppEditor editor={editor} />;
}
